import { useEffect, useRef } from 'react'
import { Box, ListItem, ListItemButton, Stack } from '@mui/material'
import { Exchange } from '../../proto/generated/common'
import { Security } from '../../proto/generated/security'
import { InstrumentLabels } from '../../util/cashInstrument'
import { ExtraSmallButtonHoverable } from '../custom'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import type { CompareSecurityPlacement } from './typings'
import { getRowButtonSx } from './rowStyles'
import SecurityRowCells from './SecurityRowCells'

const compareActionButtonSx = {
    textTransform: 'none' as const,
    fontSize: 11,
    minHeight: 10,
    height: '26px !important',
    padding: '2px 6px'
}

export interface CompareSecurityRowProps {
    security: Security
    instrumentLabels?: InstrumentLabels
    /** Exchanges shown as stacked logos (defaults to the security's own exchange). */
    exchanges?: Exchange[]
    rolling?: boolean
    searchTerm: string
    focused: boolean
    onPick: (placement: CompareSecurityPlacement) => void
}

/** Compare-picker row: the shared search row cells plus "Same % scale" / "New pane" actions on hover. */
const CompareSecurityRow = ({ security, instrumentLabels, exchanges, rolling, searchTerm, focused, onPick }: CompareSecurityRowProps) => {
    const targetRef = useRef<HTMLLIElement>(null)
    const symbol = security.symbol || ''

    useEffect(() => {
        if (focused && targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
        }
    }, [focused])

    const actions = (
        <Box className="compare-security-actions" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
                <ExtraSmallButtonHoverable
                    variant={'outlined'}
                    color={'inherit'}
                    size={'small'}
                    sx={compareActionButtonSx}
                    onClick={(e) => {
                        e.stopPropagation()
                        onPick('same_percentage_scale')
                    }}
                >
                    Same % scale
                </ExtraSmallButtonHoverable>
                <ExtraSmallButtonHoverable
                    variant={'outlined'}
                    color={'inherit'}
                    size={'small'}
                    sx={compareActionButtonSx}
                    onClick={(e) => {
                        e.stopPropagation()
                        onPick('new_pane')
                    }}
                >
                    New pane
                </ExtraSmallButtonHoverable>
            </Stack>
        </Box>
    )

    return (
        <ListItem
            disablePadding
            className={'security-item compare-security-row'}
            sx={(t) => ({
                borderBottom: 0.5,
                borderColor: 'divider',
                '& .compare-security-actions': {
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: t.transitions.create('opacity', { duration: t.transitions.duration.shorter })
                },
                '&:hover .compare-security-actions, &:focus-within .compare-security-actions': {
                    opacity: 1,
                    pointerEvents: 'auto'
                },
                ...(focused ? { '& .compare-security-actions': { opacity: 1, pointerEvents: 'auto' } } : {})
            })}
            onClick={() => {
                AnalyticsManager.logEvent('Symbol Clicked', { source: 'compare-security-picker', symbol })
                onPick('same_percentage_scale')
            }}
            ref={targetRef}
        >
            <ListItemButton disableGutters={true} sx={getRowButtonSx(focused)}>
                <SecurityRowCells
                    symbol={symbol}
                    series={security.series}
                    displayName={security.displayName || ''}
                    exchange={security.exchange ?? Exchange.NSE}
                    exchanges={exchanges}
                    instrument={security.instrument}
                    instrumentLabels={instrumentLabels}
                    rolling={rolling}
                    logoUrl={security.logoUrl}
                    searchTerm={searchTerm}
                    trailingStart={actions}
                />
            </ListItemButton>
        </ListItem>
    )
}

export default CompareSecurityRow
